import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMinutes, parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { datetimeLocalToIso, isoToDatetimeLocal } from '../../utils/datetime-local';
import type { CompetitionRound } from '../../types/competition-round';

interface DuplicateRoundDialogProps {
  round: CompetitionRound;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Makes the copy. Throws on failure, which leaves the dialog open and filled in. */
  onDuplicate: (data: { name: string; start_time: string | null }) => Promise<void>;
}

/**
 * Where the copy is proposed to start: when the original ends, which is what
 * the next turn of the same contest usually is. Blank when the original has no
 * start or no length to go by.
 */
function nextSlot(round: CompetitionRound): string {
  if (!round.start_time || !round.duration_minutes) return '';
  return isoToDatetimeLocal(addMinutes(parseISO(round.start_time), round.duration_minutes).toISOString());
}

/**
 * Another round set up like this one.
 *
 * Only the two things that tell a copy apart from its original are asked — what
 * it is called and when it starts. Everything else (the length, the classes, the
 * problems with their holds, photos and judges, whether it is a turn) comes
 * across on the server; what happened on the original does not.
 */
export default function DuplicateRoundDialog({
  round,
  open,
  onOpenChange,
  onDuplicate,
}: DuplicateRoundDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(t('gymAdmin.rounds.duplicate.defaultName', { name: round.name }));
    setStartTime(nextSlot(round));
  }, [open, round, t]);

  const valid = name.trim() !== '';

  const handleDuplicate = async () => {
    if (!valid || saving) return;

    setSaving(true);
    try {
      await onDuplicate({ name: name.trim(), start_time: datetimeLocalToIso(startTime) });
      onOpenChange(false);
    } catch {
      // Said by the caller; the dialog stays open with what was typed.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('gymAdmin.rounds.duplicate.title')}</DialogTitle>
          <DialogDescription>{t('gymAdmin.rounds.duplicate.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="duplicate-round-name">{t('gymAdmin.rounds.name')}</Label>
            <Input
              id="duplicate-round-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              data-testid="duplicate-round-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duplicate-round-start">{t('gymAdmin.rounds.startTime')}</Label>
            <Input
              id="duplicate-round-start"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              data-testid="duplicate-round-start"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDuplicate} disabled={!valid || saving} data-testid="confirm-duplicate-round">
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('gymAdmin.rounds.duplicate.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
